import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DataTableProvider } from 'components/DataTable'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
  FBPageInnerTabsContainer,
} from 'components/FbUI/Page/styles'
import { cn } from 'components/UI/cn'
import { Tabs } from 'components/FbUI/Tabs'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { useGetCampaignStats } from 'features/campaigns/campaign_stats_api'
import { CampaignsStatsBar } from 'features/campaigns/CampaignsStatsBar'
import { CampaignsTable } from 'features/campaigns/CampaignsTable'
import { CampaignRepliesTab } from 'features/campaigns/CampaignRepliesTab'
import { CreateCampaignModalV2 } from 'features/campaigns/CreateCampaignModalV2'
import { CreateEditCampaignModal } from 'features/campaigns/CreateEditCampaignModal'
import { useGetReplyThreadSummary } from 'features/campaigns/sequences_api'
import { Campaign } from 'models/campaign'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

type PageTab = 'campaigns' | 'replies'

export function CampaignsPage() {
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<PageTab>('campaigns')
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showV2Modal, setShowV2Modal] = useState<boolean>(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>()

  function handleRowEdit(campaign: Campaign) {
    setShowModal(true)
    setSelectedCampaign(campaign)
  }

  function openModal() {
    if (featureFlag.enableEmailSequencing) {
      setShowV2Modal(true)
    } else {
      setShowModal(true)
    }
  }

  function hideModal() {
    setShowModal(false)
    setSelectedCampaign(undefined)
  }

  const { data: stats } = useGetCampaignStats(featureFlag.enableEmailSequencing)
  const { data: replySummary } = useGetReplyThreadSummary()

  if (!featureFlag.isLoading && !featureFlag.enableCampaigns) {
    navigate('/')
    return null
  }

  if (featureFlag.enableEmailSequencing) {
    return (
      <DataTableProvider tableKey="campaign-overview-table">
        <FBPage>
          <FBPageHeader>
            <FBPageTitleRow>
              <div>
                <FBPageTitle>Campaigns</FBPageTitle>
                <p className="text-sm text-fg-secondary mt-0.5">
                  Manage your outreach campaigns from start to finish.
                </p>
              </div>
            </FBPageTitleRow>
            <FbButton onClick={() => openModal()}>+ New Campaign</FbButton>
          </FBPageHeader>

          <div className="pt-3">
            <CampaignsStatsBar stats={stats} />

            {/* Tabs */}
            <div className="flex gap-6 border-b border-line mb-3">
              <button
                className={cn(
                  'pb-2 text-sm font-medium transition-colors',
                  activeTab === 'campaigns'
                    ? 'border-b-2 border-brand text-brand'
                    : 'text-fg-secondary hover:text-fg'
                )}
                onClick={() => setActiveTab('campaigns')}
              >
                <span className="flex items-center gap-2">
                  All Campaigns
                  {stats?.total_campaigns != null && (
                    <span className="bg-surface-hover text-fg-secondary text-xs px-1.5 py-px rounded-full">
                      {stats.total_campaigns}
                    </span>
                  )}
                </span>
              </button>
              <button
                className={cn(
                  'pb-2 text-sm font-medium transition-colors',
                  activeTab === 'replies'
                    ? 'border-b-2 border-brand text-brand'
                    : 'text-fg-secondary hover:text-fg'
                )}
                onClick={() => setActiveTab('replies')}
              >
                <span className="flex items-center gap-2">
                  All Replies
                  {replySummary?.is_available && replySummary.total != null && (
                    <span className="bg-surface-hover text-fg-secondary text-xs px-1.5 py-px rounded-full">
                      {replySummary.total}
                    </span>
                  )}
                </span>
              </button>
            </div>

            {activeTab === 'replies' ? (
              <div
                className="flex flex-col"
                style={{
                  height: 'calc(var(--base-page-table-height) - 200px)',
                }}
              >
                <CampaignRepliesTab />
              </div>
            ) : (
              <FBPageTableContainer
                style={{
                  height: 'calc(var(--base-page-table-height) - 200px)',
                }}
              >
                <CampaignsTable
                  onEditCampaign={handleRowEdit}
                  tableKey={ALL_CAMPAIGNS_TABLE_KEY}
                />
              </FBPageTableContainer>
            )}
          </div>
        </FBPage>

        <CreateEditCampaignModal
          isOpen={showModal}
          onClose={hideModal}
          editingCampaign={selectedCampaign}
        />
        <CreateCampaignModalV2
          show={showV2Modal}
          onHide={() => setShowV2Modal(false)}
        />
      </DataTableProvider>
    )
  }

  const tabs: { key: PageTab; title: string | React.JSX.Element }[] = [
    { key: 'campaigns', title: 'Campaigns' },
    {
      key: 'replies',
      title: (
        <span>
          Replies
          {replySummary?.is_available && replySummary.total !== undefined && (
            <i className="font-extralight not-italic">
              {' '}
              ({replySummary.total})
            </i>
          )}
        </span>
      ),
    },
  ]

  return (
    <DataTableProvider tableKey="campaign-overview-table">
      <FBPage>
        <FBPageHeader>
          <FBPageTitleRow>
            <FBPageTitle>
              <span className={'font-normal'}>Campaigns</span>
            </FBPageTitle>
          </FBPageTitleRow>
          {activeTab === 'campaigns' && (
            <FbButton style={{ width: 200 }} onClick={() => openModal()}>
              Create Campaign
            </FbButton>
          )}
        </FBPageHeader>

        {featureFlag.enableEmailSequencing && (
          <FBPageInnerTabsContainer>
            <Tabs
              tabs={tabs}
              selectedTab={activeTab}
              setSelectedTab={setActiveTab}
              tabStyle={{ width: '120px' }}
            />
          </FBPageInnerTabsContainer>
        )}

        {activeTab === 'campaigns' && (
          <FBPageTableContainer
            style={{
              height: 'calc(var(--base-page-table-height) - 47px)',
            }}
          >
            <CampaignsTable
              onEditCampaign={handleRowEdit}
              tableKey={ALL_CAMPAIGNS_TABLE_KEY}
            />
          </FBPageTableContainer>
        )}

        {activeTab === 'replies' && (
          <div style={{ height: 'calc(var(--base-page-table-height) - 60px)' }}>
            <CampaignRepliesTab />
          </div>
        )}
      </FBPage>
      <CreateEditCampaignModal
        isOpen={showModal}
        onClose={hideModal}
        editingCampaign={selectedCampaign}
      />
      <CreateCampaignModalV2
        show={showV2Modal}
        onHide={() => setShowV2Modal(false)}
      />
    </DataTableProvider>
  )
}
